import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWallet } from '../api';

const badge = (status, type) => {
    const isCredit = type === 'credit';
    const isPaid = ['paid', 'paid_over', 'completed'].includes(status);
    const isPending = ['waiting', 'pending', 'processing'].includes(status);
    const bg    = isPaid ? '#d1fae5' : isPending ? '#fef3c7' : isCredit ? '#fee2e2' : '#f3f4f6';
    const color = isPaid ? '#065f46' : isPending ? '#92400e' : isCredit ? '#991b1b' : '#6b7280';
    return {
        display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11,
        fontWeight: 600, background: bg, color,
    };
};

const s = {
    h1:     { fontSize: 22, fontWeight: 700, marginBottom: 20 },
    grid:   { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' },
    card:   { background: '#fff', borderRadius: 12, padding: '24px 28px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    balLbl: { fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8, display: 'block' },
    balVal: { fontSize: 38, fontWeight: 900, color: '#1a1d23', letterSpacing: '-1px' },
    balSub: { fontSize: 13, color: '#9ca3af', marginTop: 4 },
    depBtn: { marginTop: 20, width: '100%', padding: '11px 0', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
    panel:  { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden' },
    panH:   { padding: '16px 20px', borderBottom: '1px solid #f3f4f6', fontWeight: 700, fontSize: 15, color: '#1a1d23', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    table:  { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th:     { textAlign: 'left', padding: '12px 16px', background: '#f9fafb', borderBottom: '2px solid #f3f4f6', color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: .5 },
    td:     { padding: '12px 16px', borderBottom: '1px solid #f9fafb', verticalAlign: 'middle' },
    amt:    { fontWeight: 700, fontFamily: 'monospace', fontSize: 14 },
    empty:  { padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 14 },
};

const TYPE_ICON = { credit: '⬆️', debit: '⬇️' };
const TYPE_LABEL = { credit: 'Deposit', debit: 'Order' };

export default function Wallet() {
    const navigate = useNavigate();
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState('credit');
    const [search, setSearch]         = useState('');

    const load = () => {
        setLoading(true);
        getWallet({ limit: 100 })
            .then(r => setData(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const balance = data?.balance ?? 0;
    const txs     = data?.transactions ?? [];

    const totalIn  = txs.filter(t => t.type === 'credit' && ['paid', 'paid_over'].includes(t.status)).reduce((a, t) => a + parseFloat(t.amount), 0);
    const totalOut = txs.filter(t => t.type === 'debit').reduce((a, t) => a + parseFloat(t.amount), 0);

    const sq = search.toLowerCase();
    const displayed = txs.filter(t => {
        if (typeFilter === 'credit' && t.type !== 'credit') return false;
        if (typeFilter === 'debit'  && t.type !== 'debit')  return false;
        if (sq && !t.detail?.toLowerCase().includes(sq) && !t.status?.toLowerCase().includes(sq)) return false;
        return true;
    });

    return (
        <div>
            <h1 style={s.h1}>Wallet</h1>

            <div className="og-grid-sidebar" style={s.grid}>
                {/* Balance card */}
                <div style={s.card}>
                    <span style={s.balLbl}>Available Balance</span>
                    <div style={s.balVal}>€{parseFloat(balance).toFixed(2)}</div>
                    <div style={s.balSub}>Total deposited: €{totalIn.toFixed(2)}</div>
                    <div style={{ ...s.balSub, marginTop: 2 }}>Total spent: €{totalOut.toFixed(2)}</div>
                    <button style={s.depBtn} onClick={() => navigate('/deposit')}>
                        ➕ Add Funds
                    </button>
                </div>

                {/* Transaction history */}
                <div style={s.panel}>
                    <div style={s.panH}>
                        <span>Transaction History</span>
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>{displayed.length} / {txs.length}</span>
                    </div>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}>
                            <option value="all">All</option>
                            <option value="credit">Deposits only</option>
                            <option value="debit">Orders only</option>
                        </select>
                        <input type="text" placeholder="Search description…" value={search} onChange={e => setSearch(e.target.value)}
                            style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, minWidth: 200 }} />
                    </div>

                    {loading ? (
                        <div style={s.empty}>Loading...</div>
                    ) : txs.length === 0 ? (
                        <div style={s.empty}>
                            No transactions yet.<br />
                            <span style={{ color: '#6366f1', cursor: 'pointer' }} onClick={() => navigate('/deposit')}>
                                Make your first deposit →
                            </span>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Type</th>
                                        <th style={s.th}>Description</th>
                                        <th style={s.th}>Amount</th>
                                        <th style={s.th}>Status</th>
                                        <th style={s.th}>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayed.map((t, i) => {
                                        const isCredit = t.type === 'credit';
                                        const amtColor = isCredit ? '#059669' : '#dc2626';
                                        const amtPrefix = isCredit ? '+' : '-';
                                        return (
                                            <tr key={i}>
                                                <td style={s.td}>
                                                    <span style={{ marginRight: 6 }}>{TYPE_ICON[t.type]}</span>
                                                    <span style={{ fontWeight: 600 }}>{TYPE_LABEL[t.type]}</span>
                                                </td>
                                                <td style={{ ...s.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    title={t.detail}>
                                                    {isCredit ? (
                                                        <span>{t.detail}</span>
                                                    ) : (
                                                        <span style={{ color: '#4b5563' }}>{t.detail?.replace(/^.*?—\s*/, '')?.slice(0, 35) || '—'}</span>
                                                    )}
                                                </td>
                                                <td style={{ ...s.td }}>
                                                    <span style={{ ...s.amt, color: amtColor }}>
                                                        {amtPrefix}€{parseFloat(t.amount).toFixed(isCredit ? 2 : 4)}
                                                    </span>
                                                </td>
                                                <td style={s.td}>
                                                    <span style={badge(t.status, t.type)}>{t.status}</span>
                                                </td>
                                                <td style={s.td}>
                                                    {new Date(t.created_at).toLocaleDateString('en-GB', {
                                                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                                                    })}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
